import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { useParams } from 'react-router-dom';
import { ServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/utils/types';
import {
    servicePlanBillingPolicyFormSchema,
    ServicePlanBillingPolicyFormSchemaValues,
} from 'src/features/servicePlanBillingPolicies/utils/formSchema';
import { useGetServicePlanBillingPolicy } from 'src/features/servicePlanBillingPolicies/api/useGetServicePlanBillingPolicy';
import ServicePlanBillingPolicyForm from 'src/features/servicePlanBillingPolicies/components/forms/ServicePlanBillingPolicyForm';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface ServicePlanBillingPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanBillingPolicy?: ServicePlanBillingPolicy;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ServicePlanBillingPolicyFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function ServicePlanBillingPolicyFormModal<TData>({
    selectedServicePlanBillingPolicy,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: ServicePlanBillingPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { servicePlanItemId = '' } = useParams();

    const { data: servicePlanBillingPolicy } = useGetServicePlanBillingPolicy({
        servicePlanItemId: servicePlanItemId ?? '',
        billingPolicyId: selectedServicePlanBillingPolicy?.id ?? '',
        config: { enabled: !!selectedServicePlanBillingPolicy?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            valid_from: servicePlanBillingPolicy?.valid_from ?? undefined,
            billing_cycle: servicePlanBillingPolicy?.billing_cycle ?? undefined,
            billing_time: servicePlanBillingPolicy?.billing_time ?? undefined,
            measurement_type: servicePlanBillingPolicy?.measurement_type ?? undefined,
            valid_to: servicePlanBillingPolicy?.valid_to ?? undefined,
            stateful_billing_strategy: servicePlanBillingPolicy?.stateful_billing_strategy ?? undefined,
            metered_billing_strategy: servicePlanBillingPolicy?.metered_billing_strategy ?? undefined,
            enable_proration: servicePlanBillingPolicy?.enable_proration ?? undefined,
        };
    }, [servicePlanBillingPolicy]);

    const form = useForm<ServicePlanBillingPolicyFormSchemaValues>({
        resolver: zodResolver(servicePlanBillingPolicyFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ServicePlanBillingPolicyFormSchemaValues) => {
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, values);
            mutate(
                { ...dirtyFields } as ServicePlanBillingPolicyFormSchemaValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [form.formState.dirtyFields, form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ServicePlanBillingPolicyForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedServicePlanBillingPolicy && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ServicePlanBillingPolicyFormModal;
